import { action, makeObservable, observable, onBecomeObserved } from "mobx"

import { notify } from "@src/app/components/notify"
import { bankAccountsApi, catchErrors } from "@src/shared/api"
import {
  BankAccountDetails,
  BankAccountResponse,
  BankAccountResponseCurrencyEnum,
  BankAccountResponseTransferEnum,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"

export type BankAccountForm = {
  bank_address: string
  bank_name: string
  beneficiary_account_name: string
  beneficiary_account_number: string
  beneficiary_address: string
  bic_swift_code: string
  currency: BankAccountResponseCurrencyEnum
  name: string
  transfer: BankAccountResponseTransferEnum
}

export class BankAccount extends loading(["load", "create"]) {
  state: BankAccountResponse = {}

  constructor(id: string = "") {
    super()

    makeObservable(this, {
      create: action.bound,
      load: action.bound,
      state: observable.shallow,
    })

    onBecomeObserved(this, "isLoading", () => {
      if (id) {
        this.load(id).then(() => this.setIsReadonly(true))
      }
    })
  }

  async create(values: BankAccountForm) {
    const { currency, name, transfer, ...details } = values

    return bankAccountsApi
      .createBankAccount({
        currency: currency as any,
        details,
        name,
        transfer: transfer as any,
      })
      .then(({ data: { data: res } }) => res)
  }

  async load(id: string) {
    try {
      const {
        data: { data: response },
      } = await bankAccountsApi.getBankAccount(id)

      if (response) {
        this.state = {
          ...response,
        }
      } else {
        notify.error()
      }
    } catch (e: any) {
      catchErrors(e)
    }
  }

  get isNew() {
    return !this.state.id
  }

  get toForm(): BankAccountForm {
    const { details } = this.state

    if (details) {
      const {
        bank_address,
        bank_name,
        beneficiary_account_name,
        beneficiary_account_number,
        beneficiary_address,
        bic_swift_code,
      } = details as Required<BankAccountDetails>

      return {
        bank_address: bank_address,
        bank_name: bank_name,
        beneficiary_account_name: beneficiary_account_name,
        beneficiary_account_number: beneficiary_account_number,
        beneficiary_address: beneficiary_address,
        bic_swift_code: bic_swift_code,
        currency: this.state.currency ?? BankAccountResponseCurrencyEnum.Eur,
        name: this.state.name ?? "",
        transfer: this.state.transfer ?? BankAccountResponseTransferEnum.Sepa,
      }
    }

    return {} as BankAccountForm
  }
}
